import React, { useEffect, useState } from "react";
import * as router from "react-router-dom";
import i18n from "../../i18n";
import Alert from "../../components/common/Alert";
import CrudManager from "../../components/common/CrudManager";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import fetchWithAuth from "../../utils/httpInterceptor";

export const MODULE_EXTENSION_EVENT = "idaxModuleExtensionRegistered";

const commonSdk = {
  React,
  router,
  i18n,
  fetchWithAuth,
  components: { Alert, CrudManager, Modal, PageHeader, Spinner },
};

const mergeSdk = (extension = {}) => ({
  ...(window.__IDAX_MODULE_SDK__ || {}),
  ...commonSdk,
  ...extension,
  components: {
    ...commonSdk.components,
    ...(window.__IDAX_MODULE_SDK__?.components || {}),
    ...(extension.components || {}),
  },
  icons: {
    ...(window.__IDAX_MODULE_SDK__?.icons || {}),
    ...(extension.icons || {}),
  },
});

const registeredComponent = (moduleKey) =>
  window.__IDAX_MODULE_EXTENSIONS__?.[moduleKey]?.component || null;

export const loadModuleExtension = ({ moduleKey, scriptUrl, styleUrl, sdk = {}, timeoutMs = 10000 }) => {
  const registered = registeredComponent(moduleKey);
  if (registered) return Promise.resolve(registered);
  window.__IDAX_MODULE_SDK__ = mergeSdk(sdk);

  if (styleUrl && !document.querySelector(`link[data-idax-module="${moduleKey}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = styleUrl;
    link.dataset.idaxModule = moduleKey;
    document.head.appendChild(link);
  }

  return new Promise((resolve, reject) => {
    let timeoutId;
    const finish = (callback, value) => {
      window.removeEventListener(MODULE_EXTENSION_EVENT, onRegistered);
      if (timeoutId) window.clearTimeout(timeoutId);
      callback(value);
    };
    const fail = (reason) => finish(reject, new Error(reason));
    const onRegistered = (event) => {
      if (event.detail?.module !== moduleKey) return;
      const component = registeredComponent(moduleKey);
      if (component) finish(resolve, component);
      else fail(`${moduleKey} extension registered without a component`);
    };

    window.addEventListener(MODULE_EXTENSION_EVENT, onRegistered);
    timeoutId = window.setTimeout(
      () => fail(`Timed out while loading the ${moduleKey} frontend extension`),
      timeoutMs
    );

    const existing = document.querySelector(`script[data-idax-module="${moduleKey}"]`);
    if (existing) {
      if (existing.dataset.failed === "true") {
        existing.remove();
        fail(`${moduleKey} frontend extension failed to load`);
      }
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.dataset.idaxModule = moduleKey;
    script.onerror = () => {
      script.dataset.failed = "true";
      fail(`${moduleKey} frontend extension is unavailable`);
    };
    document.head.appendChild(script);
  });
};

export const createModuleExtensionHost = (config) => {
  const ModuleExtensionHost = () => {
    const [state, setState] = useState({ component: registeredComponent(config.moduleKey), error: null });
    const [tenantRevision, setTenantRevision] = useState(0);

    useEffect(() => {
      let mounted = true;
      loadModuleExtension(config)
        .then((component) => mounted && setState({ component, error: null }))
        .catch((error) => mounted && setState({ component: null, error: error.message }));
      return () => { mounted = false; };
    }, []);
    useEffect(() => {
      const handleTenantChanged = () => setTenantRevision((value) => value + 1);
      window.addEventListener("tenantChanged", handleTenantChanged);
      return () => window.removeEventListener("tenantChanged", handleTenantChanged);
    }, []);

    if (state.error) return <Alert type="error" message={state.error} />;
    if (!state.component) return <Spinner overlay={false} size="medium" />;
    const Extension = state.component;
    return <Extension key={`${config.moduleKey}-tenant-${tenantRevision}`} />;
  };
  ModuleExtensionHost.displayName = `${config.moduleKey}ModuleExtensionHost`;
  return ModuleExtensionHost;
};
